import { useState, useEffect } from 'react';
import React from 'react';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [formData, setFormData] = useState({
    // A) Basic Info
    fullName: '',
    email: '',
    phone: '',
    businessName: '',
    websiteLink: '',
    
    // B) Business Stage and Offer
    businessStage: '',
    whatYouSell: '',
    targetCustomer: '',
    biggestChallenges: [] as string[],
    
    // C) Revenue and Capacity
    currentRevenue: '',
    clientsPerMonth: '',
    focusService: '',
    
    // D) Systems and Financial Readiness
    businessBankAccount: '',
    trackExpenses: '',
    separatedFinances: '',
    bookkeepingTool: '',
    filedBusinessTaxes: '',
    oweTaxes: '',
    
    // E) Program Fit and Readiness
    whyJoin: '',
    successMetrics: [] as string[],
    stoppedYou: '',
    weeklyCommitment: '',
    comfortableWithHomework: '',
    thursdayCommitment: '',
    sessionFormat: '',
    startTiming: '',
    
    // F) Logistics + Consent
    hearAbout: '',
    additionalInfo: '',
    consent: false,
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const [waitlistData, setWaitlistData] = useState({
    fullName: '',
    email: '',
    phone: '',
    businessStage: '',
    topGoal: '',
  });
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [waitlistError, setWaitlistError] = useState('');

  // -------------------------------------------------------------------------
  // Scroll handling – adds a sticky CTA bar after the user scrolls past 800px
  // -------------------------------------------------------------------------
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 800);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // -------------------------------------------------------------------------
  // FAQ toggle
  // -------------------------------------------------------------------------
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // -------------------------------------------------------------------------
  // Form input handling – supports <input>, <textarea> and <select>
  // -------------------------------------------------------------------------
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      if (name === 'consent') {
        setFormData((prev) => ({ ...prev, [name]: target.checked }));
      } else if (name === 'biggestChallenges' || name === 'successMetrics') {
        const currentArray = formData[name] as string[];
        if (target.checked) {
          setFormData((prev) => ({
            ...prev,
            [name]: [...currentArray, value],
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            [name]: currentArray.filter((item) => item !== value),
          }));
        }
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // -------------------------------------------------------------------------
  // Form submission – robust error handling + user feedback
  // -------------------------------------------------------------------------
  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();
    setFormError('');
    setFormSubmitted(false);

    // Basic client‑side validation
    const {
      fullName,
      email,
      phone,
      businessStage,
      whatYouSell,
      targetCustomer,
      currentRevenue,
      whyJoin,
      consent,
    } = formData;
    
    if (!fullName || !email || !phone || !businessStage || !whatYouSell || !targetCustomer || !currentRevenue || !whyJoin) {
      setFormError('Please fill in all required fields.');
      return;
    }
    
    if (!consent) {
      setFormError('Please agree to the program terms to continue.');
      return;
    }

    try {
      const formBody = new URLSearchParams();
      
      // Convert arrays to comma-separated strings
      const submissionData = {
        ...formData,
        biggestChallenges: formData.biggestChallenges.join(', '),
        successMetrics: formData.successMetrics.join(', '),
        consent: formData.consent ? 'Yes' : 'No',
      };
      
      Object.entries(submissionData).forEach(([key, value]) => {
        formBody.append(key, String(value));
      });

      const response = await fetch(
        'https://readdy.ai/api/form/d6a7ksfmvg9ih2c7aco0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody.toString(),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Server responded with ${response.status}: ${errorText}`
        );
      }

      // Success – reset form & show thank‑you state
      setFormSubmitted(true);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        businessName: '',
        websiteLink: '',
        businessStage: '',
        whatYouSell: '',
        targetCustomer: '',
        biggestChallenges: [],
        currentRevenue: '',
        clientsPerMonth: '',
        focusService: '',
        businessBankAccount: '',
        trackExpenses: '',
        separatedFinances: '',
        bookkeepingTool: '',
        filedBusinessTaxes: '',
        oweTaxes: '',
        whyJoin: '',
        successMetrics: [],
        stoppedYou: '',
        weeklyCommitment: '',
        comfortableWithHomework: '',
        thursdayCommitment: '',
        sessionFormat: '',
        startTiming: '',
        hearAbout: '',
        additionalInfo: '',
        consent: false,
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setFormError(
        'Something went wrong while sending the form. Please try again later.'
      );
    }
  };

  // -------------------------------------------------------------------------
  // Smooth scroll to the application form
  // -------------------------------------------------------------------------
  const scrollToApplication = () => {
    document
      .getElementById('waitlist')
      ?.scrollIntoView({ behavior: 'smooth' });
  };

  // -------------------------------------------------------------------------
  // Waitlist form handlers
  // -------------------------------------------------------------------------
  const handleWaitlistChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setWaitlistData((prev) => ({ ...prev, [name]: value }));
  };

  const handleWaitlistSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setWaitlistError('');

    if (!waitlistData.fullName || !waitlistData.email) {
      setWaitlistError('Please provide your full name and email to join the waitlist.');
      return;
    }

    try {
      const body = new URLSearchParams();
      Object.entries(waitlistData).forEach(([key, value]) => body.append(key, value));

      const response = await fetch('https://readdy.ai/api/form/d76jm2l4hjod66j2esag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (!response.ok) {
        throw new Error(`Server error ${response.status}`);
      }

      setWaitlistSubmitted(true);
      setWaitlistData({ fullName: '', email: '', phone: '', businessStage: '', topGoal: '' });
    } catch (err) {
      console.error('Waitlist submission error:', err);
      setWaitlistError('Something went wrong. Please try again.');
    }
  };

  // -------------------------------------------------------------------------
  // Static data – weeks, FAQs, etc.
  // -------------------------------------------------------------------------
  const weeks = [
    {
      num: 1,
      title: 'Foundation + Business Setup',
      items: [
        'Business structure options and setup roadmap',
        'Licenses, permits, and legal requirements',
        'Define your offer and target customer',
        'Create your value proposition',
      ],
    },
    {
      num: 2,
      title: 'Finance + Tax Readiness',
      items: [
        'Pricing strategy and revenue structure',
        'Financial tracking systems and tools',
        'Tax preparation and quarterly planning',
        'Capital and Funding Readiness',
      ],
    },
    {
      num: 3,
      title: 'Branding + Marketing Implementation',
      items: [
        'Brand identity and messaging framework',
        'Marketing channels and content strategy',
        'Client acquisition systems',
        'Social media and online presence setup',
      ],
    },
    {
      num: 4,
      title: 'AI Integration + Operating Systems',
      items: [
        'Back‑office systems (intake, scheduling, invoicing)',
        'File organization and document management',
        'AI tools for efficiency and automation',
        'Standard operating procedures (SOPs)',
      ],
    },
    {
      num: 5,
      title: 'Sustainability + Put It All Together',
      items: [
        '90‑day operating plan creation',
        'Mini business plan completion',
        'Pitch presentation development',
        'Next steps and growth strategy',
      ],
    },
  ];

  const faqs = [
    {
      question: 'Do I need an LLC before joining?',
      answer:
        "No, you don't need an LLC before joining. We cover business structure options and setup in Week 1, so you'll learn what's best for your situation and how to set it up properly.",
    },
    {
      question: 'Is this program for beginners?',
      answer:
        'Yes! This program is designed for early‑stage businesses with at least 6 months running business informally or formally. Whether you\'re just starting or have been operating informally, you\'ll get the structure and systems you need to operate professionally.',
    },
    {
      question: 'What if I miss a session?',
      answer:
        'All sessions are live and participation is encouraged for the best experience. Make sure to attend each week to get the most value and stay on track with implementation. To qualify for the stipend all 5 sessions must have attendance,',
    },
    {
      question: 'Is this industry‑specific?',
      answer:
        'This program works best for service‑based businesses across all industries. The systems, financial tracking, and operating principles apply whether you\'re a consultant, coach, designer, or any other service provider.',
    },
    {
      question: 'What do I need to participate?',
      answer:
        "Attendance and the willingness to implement what you learn. This is a done‑with‑you program, so you'll be taking action each week. Note taking is encouraged.&nbsp;",
    },
    {
      question: 'How do I apply?',
      answer:
        'Simply fill out the application form below. We\'ll review your application and contact you within 2 weeks of start date to discuss next steps and answer any questions you have.',
    },
    {
      question: 'Is there support between sessions?',
      answer:
        'Yes! You\'ll have access to Q&A support throughout the program, plus a private community where you can ask questions and connect with other participants.',
    },
    {
      question: "What's the time commitment?",
      answer:
        'Plan for 2–3 hours per week: 1 hour for the live session and 1–2 hours for implementation and homework. The more you put in, the more you\'ll get out of the program.',
    },
    {
      question: 'What are the stipends?',
      answer:
        'The top business during the session will receive a $1,000 stipend to invest in their business. This is our way of supporting your entrepreneurial journey.',
    },
  ];

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://static.readdy.ai/image/ac013c1ee8610b2ee6c1b91fe75f40f6/533b468db4011c652daa16bcc3a5c3fa.jpeg"
              alt="From Scratch Logo"
              className="w-10 h-10 object-contain"
            />
            <span className="text-gray-300 hidden md:block">|</span>
            <img
              src="https://static.readdy.ai/image/ac013c1ee8610b2ee6c1b91fe75f40f6/d3c89306c24de7dfc1bbb2f80f0a994f.png"
              alt="BBRC - Birmingham Business Resource Center"
              className="h-10 object-contain hidden md:block"
            />
          </div>

          <div className="hidden lg:flex items-center gap-8">
            <a
              href="#about"
              className="text-sm text-gray-700 hover:text-[#1B3A5C] transition-colors cursor-pointer whitespace-nowrap"
            >
              About
            </a>
            <a
              href="#program"
              className="text-sm text-gray-700 hover:text-[#1B3A5C] transition-colors cursor-pointer whitespace-nowrap"
            >
              Program
            </a>
            <a
              href="#faq"
              className="text-sm text-gray-700 hover:text-[#1B3A5C] transition-colors cursor-pointer whitespace-nowrap"
            >
              FAQ
            </a>
            <a
              href="#waitlist"
              className="text-sm text-[#E8553D] font-semibold hover:text-[#D44A33] transition-colors cursor-pointer whitespace-nowrap"
            >
              Join Waitlist
            </a>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={scrollToApplication}
              className="bg-[#E8553D] text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-[#D44A33] transition-colors whitespace-nowrap cursor-pointer"
            >
              Join Waitlist
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://static.readdy.ai/image/ac013c1ee8610b2ee6c1b91fe75f40f6/d1bc0b285beeaf3f447424c46b651194.jpeg"
            alt="Hero background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B3A5C]/90 via-[#1B3A5C]/75 to-[#1B3A5C]/40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-28 md:py-36">
          <div className="max-w-3xl w-full">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
              Master the Fundamentals of Running a
              <span className="text-[#F5C542]">&nbsp;Business</span>&nbsp;the Right Way
            </h1>

            <p className="text-lg md:text-xl text-white/85 leading-relaxed mb-4 max-w-2xl">
              A done‑with‑you program to set up your business foundation,
              build your back‑office systems, get financially ready, and leave
              with a clear plan and pitch.
            </p>

            <div className="border-l-4 border-[#F5C542] pl-4 mb-10">
              <p className="text-white/70 italic">
                Built for real implementation, not theory. For Business Owners BY Business Owners
              </p>
            </div>

            <div className="flex flex-wrap gap-4 mb-12">
              <button
                onClick={scrollToApplication}
                className="bg-[#E8553D] text-white px-8 py-4 rounded-md text-lg font-semibold hover:bg-[#D44A33] transition-all hover:shadow-lg hover:shadow-[#E8553D]/30 whitespace-nowrap cursor-pointer"
              >
                Join Waitlist
              </button>
            </div>

            {/* Partner Logos Row */}
            <div className="flex items-center gap-5 pt-6 border-t border-white/15">
              <span className="text-white/50 text-xs uppercase tracking-wider">
                Presented by
              </span>
              <img
                src="https://static.readdy.ai/image/ac013c1ee8610b2ee6c1b91fe75f40f6/533b468db4011c652daa16bcc3a5c3fa.jpeg"
                alt="From Scratch"
                className="h-9 object-contain rounded bg-white/90 px-2 py-1"
              />
              <span className="text-white/30 text-lg">&times;</span>
              <img
                src="https://static.readdy.ai/image/ac013c1ee8610b2ee6c1b91fe75f40f6/d3c89306c24de7dfc1bbb2f80f0a994f.png"
                alt="BBRC"
                className="h-9 object-contain rounded bg-white/90 px-2 py-1"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Banner */}
      <section className="bg-gradient-to-r from-[#1B3A5C] to-[#2A5A8C] py-6 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-handshake-line text-2xl text-[#F5C542]"></i>
            </div>
            <p className="text-white font-medium text-sm">
              A proud partnership between{' '}
              <strong>From Scratch Business Advisory Services</strong> and the{' '}
              <strong>Birmingham Business Resource Center (BBRC)</strong>
            </p>
          </div>
          <span className="text-white/50 text-xs hidden md:block">|</span>
          <p className="text-[#F5C542] text-sm font-medium italic">
            Your One‑Stop Center for Small Business Finance
          </p>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6">
            <span className="inline-block bg-[#E8553D]/10 text-[#E8553D] text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-4">
              WHO IT&apos;S FOR
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Built for entrepreneurs ready to get serious
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
              This program is designed for people who have a skill but need the
              structure, systems, and financial readiness to turn it into a real
              business.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {[
              {
                icon: 'ri-rocket-line',
                title: 'New Entrepreneurs',
                desc: 'Starting from scratch and need a clear roadmap',
                color: 'from-[#E8553D] to-[#F5C542]',
              },
              {
                icon: 'ri-briefcase-line',
                title: 'Side Hustlers',
                desc: 'Ready to become official and scale up',
                color: 'from-[#1B3A5C] to-[#2A8C7A]',
              },
              {
                icon: 'ri-service-line',
                title: 'Service Providers',
                desc: 'Making money but lacking systems and structure',
                color: 'from-[#2A8C7A] to-[#4ECDC4]',
              },
              {
                icon: 'ri-organization-chart',
                title: 'Business Owners',
                desc: 'Need pricing clarity and financial readiness',
                color: 'from-[#F5C542] to-[#E8553D]',
              },
            ].map((item, i) => (
              <div key={i} className="relative group">
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${item.color} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
                ></div>
                <div className="relative bg-gray-50 group-hover:bg-transparent rounded-2xl p-8 text-center transition-all duration-300 border border-gray-100 group-hover:border-transparent">
                  <div className="w-16 h-16 mx-auto mb-5 rounded-xl bg-white shadow-sm flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <i
                      className={`${item.icon} text-3xl text-[#1B3A5C] group-hover:text-white transition-colors`}
                    ></i>
                  </div>
                  <h3 className="font-bold text-gray-900 group-hover:text-white mb-2 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-500 group-hover:text-white/80 text-sm transition-colors">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You'll Leave With Section */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1B3A5C] via-[#1B3A5C] to-[#2A8C7A]"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#F5C542]/10 rounded-full blur-3xl -mr-48 -mt-48"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#E8553D]/10 rounded-full blur-3xl -ml-48 -mb-48"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-5 gap-12 items-start">
            <div className="md:col-span-2">
              <span className="inline-block bg-[#F5C542]/20 text-[#F5C542] text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-6">
                OUTCOMES
              </span>
              <h2 className="text-5xl font-bold text-white mb-4">
                What You&apos;ll Leave With
              </h2>
              <div className="w-4/5 h-1 bg-gradient-to-r from-[#E8553D] to-[#F5C542] mb-6 rounded-full"></div>
              <p className="text-white/60 text-lg leading-relaxed">
                Real outcomes you can implement immediately after completing
                the program.
              </p>
            </div>
            <div className="md:col-span-3 space-y-5">
              {[
                'Business setup roadmap (structure, licenses, next steps)',
                'Defined offer + target customer',
                'Pricing and revenue structure (packages/tier options)',
                'Back‑office systems (intake, scheduling, invoicing, file organization)',
                'Financial tracking habits + tax readiness workflow',
                'Simple 90‑day operating plan',
                'Mini business plan + pitch presentation completed',
                '$1,000 stipend for top business upon completion of the program',
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex gap-4 items-start bg-white/5 backdrop-blur-sm rounded-xl px-6 py-4 border border-white/10 hover:border-[#F5C542]/30 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E8553D] to-[#F5C542] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <p className="text-white text-lg">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Program Breakdown Section */}
      <section id="program" className="py-24 px-6 bg-[#FAFAF8]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-[#1B3A5C]/10 text-[#1B3A5C] text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-4">
              CURRICULUM
            </span>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">
              5‑Week Program Breakdown
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Each week builds on the last, giving you a complete business
              foundation by the end.
            </p>
          </div>

          <div className="space-y-6">
            {weeks.map((week) => {
              const colors = [
                'from-[#E8553D] to-[#F5C542]',
                'from-[#1B3A5C] to-[#2A8C7A]',
                'from-[#2A8C7A] to-[#4ECDC4]',
                'from-[#F5C542] to-[#E8553D]',
                'from-[#1B3A5C] to-[#E8553D]',
              ];
              const borderColors = [
                'border-l-[#E8553D]',
                'border-l-[#1B3A5C]',
                'border-l-[#2A8C7A]',
                'border-l-[#F5C542]',
                'border-l-[#E8553D]',
              ];
              return (
                <div
                  key={week.num}
                  className={`bg-white rounded-2xl shadow-md p-8 md:p-10 flex gap-6 md:gap-8 items-start border-l-4 ${borderColors[week.num - 1]} hover:shadow-lg transition-shadow`}
                >
                  <div className="flex-shrink-0">
                    <div
                      className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br ${colors[week.num - 1]} flex items-center justify-center shadow-lg`}
                    >
                      <span className="text-white text-xl md:text-2xl font-bold">
                        {week.num}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                      {week.title}
                    </h3>
                    <ul className="space-y-2">
                      {week.items.map((item, j) => (
                        <li
                          key={j}
                          className="flex gap-3 text-gray-600 text-sm md:text-base"
                        >
                          <span className="text-[#E8553D] font-bold mt-0.5">
                            <i className="ri-arrow-right-s-line"></i>
                          </span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Format & What's Included Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block bg-[#2A8C7A]/10 text-[#2A8C7A] text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-4">
              EVERYTHING YOU GET
            </span>
            <h2 className="text-4xl font-bold text-gray-900">
              What&apos;s Included
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5 mb-10">
            {[
              '5 live weekly sessions',
              'Worksheets, templates, and implementation tools',
              'Action steps and homework each week',
              'Q&A support throughout the program',
              'Optional 1:1 add‑on consultation available',
              'Lifetime access to program materials',
              '$1,000 stipend for top business',
            ].map((item, i) => (
              <div
                key={i}
                className="flex gap-4 items-center bg-gradient-to-r from-[#F0FAF7] to-white rounded-xl px-5 py-4 border border-[#2A8C7A]/10"
              >
                <div className="w-8 h-8 rounded-lg bg-[#2A8C7A] flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-white text-sm"></i>
                </div>
                <p className="text-gray-800 font-medium text-sm">{item}</p>
              </div>
            ))}
          </div>

          <div className="text-center bg-gradient-to-r from-[#1B3A5C] to-[#2A8C7A] rounded-2xl py-6 px-8">
            <p className="text-white font-medium italic">
              This program is done‑with‑you and focused on execution.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 px-6 bg-[#FAFAF8]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block bg-[#E8553D]/10 text-[#E8553D] text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-4">
              FAQ
            </span>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-500">
              Everything you need to know about the program
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <h4 className="font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </h4>
                  <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                    <i
                      className={`ri-arrow-${
                        openFaq === index ? 'up' : 'down'
                      }-s-line text-xl text-[#E8553D] transition-transform`}
                    ></i>
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5 pt-2">
                    <p className="text-gray-600 leading-relaxed text-sm">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="consultation" className="py-28 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E8553D] via-[#E8553D] to-[#F5C542]"></div>
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 border-2 border-white rounded-full"></div>
          <div className="absolute bottom-10 right-10 w-60 h-60 border-2 border-white rounded-full"></div>
          <div className="absolute top-1/2 left-1/3 w-20 h-20 border-2 border-white rounded-full"></div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Ready to build a business that actually works?
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed max-w-2xl mx-auto">
            Cycle 1 is closed, but Cycle 2 is coming. Join the waitlist now and be first in line when applications open.
          </p>
          <div className="flex flex-wrap gap-5 justify-center">
            <button
              onClick={scrollToApplication}
              className="bg-white text-[#E8553D] px-10 py-5 rounded-md text-lg font-bold hover:bg-gray-50 transition-all hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Join Waitlist
            </button>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section id="application-form" className="py-24 px-6 bg-[#FAFAF8]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block bg-[#E8553D]/10 text-[#E8553D] text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-4">
              GET STARTED
            </span>
            <h2 className="text-4xl font-bold text-gray-900 mb-2">
              Apply to Join
            </h2>
            <p className="text-gray-500 text-sm">
              Fill out the form below and we&apos;ll be in touch within 24&ndash;48
              hours.
            </p>
          </div>

          {/* Applications Closed Banner */}
          <div className="bg-[#1B3A5C] rounded-xl px-8 py-6 mb-8 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#F5C542]/20 flex-shrink-0">
              <i className="ri-lock-line text-2xl text-[#F5C542]"></i>
            </div>
            <div>
              <p className="text-[#F5C542] text-xs font-bold uppercase tracking-widest mb-1">Applications Closed</p>
              <p className="text-white text-lg font-bold leading-snug">
                Applications are now closed for the <span className="text-[#F5C542]">Cycle 1 Incubator</span>.
              </p>
              <p className="text-white/60 text-sm mt-1">
                Stay tuned for our next cohort announcement. Follow us on social media to be notified when applications reopen.
              </p>
            </div>
          </div>

          {formSubmitted ? (
            <div className="bg-[#2A8C7A]/10 border border-[#2A8C7A]/30 rounded-xl p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#2A8C7A] flex items-center justify-center">
                <i className="ri-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Application Received!
              </h3>
              <p className="text-gray-600">
                We&apos;ll contact you within 24&ndash;48 hours.
              </p>
            </div>
          ) : (
            <form
              id="application-form-element"
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-white rounded-2xl shadow-lg p-8 md:p-10 space-y-8 border border-gray-100"
            >
              {formError && (
                <div className="bg-[#E8553D]/10 border border-[#E8553D]/30 rounded-lg p-4">
                  <p className="text-[#E8553D] text-sm font-medium">
                    {formError}
                  </p>
                </div>
              )}

              {/* A) Basic Info */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-900 border-b-2 border-[#1B3A5C] pb-2">
                  A) Basic Info
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all text-sm"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Business Name
                    </label>
                    <input
                      type="text"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleInputChange}
                      placeholder="Not yet"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                    Website/Instagram/Facebook (link)
                  </label>
                  <input
                    type="url"
                    name="websiteLink"
                    value={formData.websiteLink}
                    onChange={handleInputChange}
                    placeholder="https://"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all text-sm"
                  />
                </div>
              </div>

              {/* B) Business Stage and Offer */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-900 border-b-2 border-[#1B3A5C] pb-2">
                  B) Business Stage and Offer
                </h3>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    Which best describes you right now? *
                  </label>
                  <select
                    name="businessStage"
                    value={formData.businessStage}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all cursor-pointer text-sm"
                  >
                    <option value="">Select your stage</option>
                    <option value="Starting from scratch (idea stage)">I&apos;m starting from scratch (idea stage)</option>
                    <option value="Side hustle (some clients, inconsistent)">I have a side hustle (some clients, inconsistent)</option>
                    <option value="Operating consistently (ready to systemize)">I&apos;m operating consistently (ready to systemize)</option>
                    <option value="Established but disorganized (cleanup needed)">I&apos;m established but disorganized (cleanup needed)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    What do you sell (service/product)? Describe in 1–2 sentences. *
                  </label>
                  <textarea
                    name="whatYouSell"
                    value={formData.whatYouSell}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all text-sm resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.whatYouSell.length}/500 characters</p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    Who is your target customer right now? (Be specific, not &quot;everyone&quot;.) *
                  </label>
                  <textarea
                    name="targetCustomer"
                    value={formData.targetCustomer}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all text-sm resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.targetCustomer.length}/500 characters</p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                    What is your biggest challenge today? (Pick up to 2)
                  </label>
                  <div className="space-y-2">
                    {[
                      'Business setup/legal structure',
                      'Pricing and profitability',
                      'Getting consistent clients',
                      'Systems, admin, and time management',
                      'Financial tracking/bookkeeping',
                      'Tax readiness/compliance',
                      'Branding and marketing clarity',
                      "I don't know where to start",
                    ].map((challenge) => (
                      <label key={challenge} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="biggestChallenges"
                          value={challenge}
                          checked={formData.biggestChallenges.includes(challenge)}
                          onChange={handleInputChange}
                          disabled={
                            formData.biggestChallenges.length >= 2 &&
                            !formData.biggestChallenges.includes(challenge)
                          }
                          className="w-4 h-4 text-[#1B3A5C] border-gray-300 rounded focus:ring-[#1B3A5C] cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">{challenge}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* C) Revenue and Capacity */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-900 border-b-2 border-[#2A8C7A] pb-2">
                  C) Revenue and Capacity
                </h3>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    Are you currently making money from your business? *
                  </label>
                  <select
                    name="currentRevenue"
                    value={formData.currentRevenue}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all cursor-pointer text-sm"
                  >
                    <option value="">Select revenue range</option>
                    <option value="Not yet">Not yet</option>
                    <option value="Yes, under $1,000/month">Yes, under $1,000/month</option>
                    <option value="Yes, $1,000–$5,000/month">Yes, $1,000–$5,000/month</option>
                    <option value="Yes, $5,000–$10,000/month">Yes, $5,000–$10,000/month</option>
                    <option value="Yes, $10,000+/month">Yes, $10,000+/month</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    How many clients/orders do you handle per month (average)?
                  </label>
                  <input
                    type="text"
                    name="clientsPerMonth"
                    value={formData.clientsPerMonth}
                    onChange={handleInputChange}
                    placeholder="e.g., 5 clients, 20 orders"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    What is your #1 service/product you want to focus on during this program?
                  </label>
                  <input
                    type="text"
                    name="focusService"
                    value={formData.focusService}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all text-sm"
                  />
                </div>
              </div>

              {/* D) Systems and Financial Readiness */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-900 border-b-2 border-[#F5C542] pb-2">
                  D) Systems and Financial Readiness
                </h3>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Do you currently have a business bank account?
                    </label>
                    <select
                      name="businessBankAccount"
                      value={formData.businessBankAccount}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all cursor-pointer text-sm"
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                      <option value="Not sure">Not sure</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Do you track income and expenses consistently?
                    </label>
                    <select
                      name="trackExpenses"
                      value={formData.trackExpenses}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all cursor-pointer text-sm"
                    >
                      <option value="">Select</option>
                      <option value="Yes, weekly or monthly">Yes, weekly or monthly</option>
                      <option value="Sometimes">Sometimes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    Are your finances currently separated from personal spending?
                  </label>
                  <select
                    name="separatedFinances"
                    value={formData.separatedFinances}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all cursor-pointer text-sm"
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Somewhat">Somewhat</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    What bookkeeping tool (if any) are you using?
                  </label>
                  <select
                    name="bookkeepingTool"
                    value={formData.bookkeepingTool}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all cursor-pointer text-sm"
                  >
                    <option value="">Select</option>
                    <option value="QuickBooks">QuickBooks</option>
                    <option value="Excel/Google Sheets">Excel/Google Sheets</option>
                    <option value="Notes/Cash App/Zelle only">Notes/Cash App/Zelle only</option>
                    <option value="Other">Other</option>
                    <option value="None">None</option>
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Have you filed business taxes before?
                    </label>
                    <select
                      name="filedBusinessTaxes"
                      value={formData.filedBusinessTaxes}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all cursor-pointer text-sm"
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                      <option value="Not sure / someone else did it">Not sure / someone else did it</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Do you currently owe taxes at the end of the year?
                    </label>
                    <select
                      name="oweTaxes"
                      value={formData.oweTaxes}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all cursor-pointer text-sm"
                    >
                      <option value="">Select</option>
                      <option value="Usually yes">Usually yes</option>
                      <option value="Usually no">Usually no</option>
                      <option value="Not sure">Not sure</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* E) Program Fit and Readiness */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-900 border-b-2 border-[#E8553D] pb-2">
                  E) Program Fit and Readiness
                </h3>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    Why do you want to join this program right now? (2–4 sentences) *
                  </label>
                  <textarea
                    name="whyJoin"
                    value={formData.whyJoin}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all text-sm resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.whyJoin.length}/500 characters</p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                    What would &quot;success&quot; look like by the end of 5 weeks? (Pick 3)
                  </label>
                  <div className="space-y-2">
                    {[
                      'Official setup plan completed (structure + licensing roadmap)',
                      'Clear offer + target customer',
                      'Pricing/package structure finalized',
                      'Back‑office systems (intake, scheduling, invoicing, file organization)',
                      'Financial tracking system in place',
                      'Tax readiness workflow established',
                      'Pitch/business plan completed',
                      '90-day action plan completed',
                    ].map((metric) => (
                      <label key={metric} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="successMetrics"
                          value={metric}
                          checked={formData.successMetrics.includes(metric)}
                          onChange={handleInputChange}
                          disabled={
                            formData.successMetrics.length >= 3 &&
                            !formData.successMetrics.includes(metric)
                          }
                          className="w-4 h-4 text-[#1B3A5C] border-gray-300 rounded focus:ring-[#1B3A5C] cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">{metric}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    What has stopped you from getting organized up to this point?
                  </label>
                  <select
                    name="stoppedYou"
                    value={formData.stoppedYou}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all cursor-pointer text-sm"
                  >
                    <option value="">Select</option>
                    <option value="Time">Time</option>
                    <option value="Knowledge">Knowledge</option>
                    <option value="Overwhelm">Overwhelm</option>
                    <option value="Money">Money</option>
                    <option value="Consistency/discipline">Consistency/discipline</option>
                    <option value="Fear/uncertainty">Fear/uncertainty</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Can you commit 3–5 hours per week to implementation outside of sessions?
                    </label>
                    <select
                      name="weeklyCommitment"
                      value={formData.weeklyCommitment}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all cursor-pointer text-sm"
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Are you comfortable with homework and deadlines?
                    </label>
                    <select
                      name="comfortableWithHomework"
                      value={formData.comfortableWithHomework}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all cursor-pointer text-sm"
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    Can you commit to 2 hours every Thursday evening at 5:30 PM for all 5 sessions?
                  </label>
                  <select
                    name="thursdayCommitment"
                    value={formData.thursdayCommitment}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all cursor-pointer text-sm"
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Most weeks, but not all">Most weeks, but not all</option>
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Which session format do you prefer?
                    </label>
                    <select
                      name="sessionFormat"
                      value={formData.sessionFormat}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all cursor-pointer text-sm"
                    >
                      <option value="">Select</option>
                      <option value="In-person (Birmingham)">In-person (Birmingham)</option>
                      <option value="Virtual">Virtual</option>
                      <option value="Either">Either</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      How soon are you trying to start?
                    </label>
                    <select
                      name="startTiming"
                      value={formData.startTiming}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all cursor-pointer text-sm"
                    >
                      <option value="">Select</option>
                      <option value="Immediately">Immediately</option>
                      <option value="Within 30 days">Within 30 days</option>
                      <option value="60+ days">60+ days</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* F) Logistics + Consent */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-900 border-b-2 border-[#2A8C7A] pb-2">
                  F) Logistics + Consent
                </h3>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    How did you hear about From Scratch?
                  </label>
                  <input
                    type="text"
                    name="hearAbout"
                    value={formData.hearAbout}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    Anything else you want us to know about your business situation?
                  </label>
                  <textarea
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleInputChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all text-sm resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.additionalInfo.length}/500 characters</p>
                </div>

                <div className="bg-[#F5C542]/10 border border-[#F5C542]/30 rounded-lg p-5">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="consent"
                      checked={formData.consent}
                      onChange={handleInputChange}
                      required
                      className="w-5 h-5 text-[#1B3A5C] border-gray-300 rounded focus:ring-[#1B3A5C] cursor-pointer mt-0.5 flex-shrink-0"
                    />
                    <span className="text-sm text-gray-700">
                      I understand this is a done-with-you program focused on implementation, and results depend on my participation. *
                    </span>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-[#E8553D] to-[#D44A33] text-white px-8 py-4 rounded-lg text-base font-bold hover:from-[#D44A33] hover:to-[#C03F28] transition-all hover:shadow-lg whitespace-nowrap cursor-pointer"
              >
                Submit Application
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Cycle 2 Waitlist Section */}
      <section id="waitlist" className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl overflow-hidden border border-gray-100">
            {/* Top color bar */}
            <div className="bg-gradient-to-r from-[#1B3A5C] to-[#2A8C7A] px-10 py-10 text-center">
              <span className="inline-block bg-white/20 text-white text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-5">
                COMING SOON
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-3 leading-tight">
                Join the Cycle 2 Waitlist
              </h2>
              <p className="text-white/75 text-lg max-w-xl mx-auto">
                Cycle 1 is full — but your spot in the next cohort is waiting. Be the first to know when applications open for <span className="text-[#F5C542] font-semibold">Cycle 2</span>.
              </p>
            </div>

            {/* Form area */}
            <div className="bg-[#FAFAF8] px-8 md:px-12 py-10">
              {waitlistSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#2A8C7A] flex items-center justify-center">
                    <i className="ri-check-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">You&apos;re on the list!</h3>
                  <p className="text-gray-500 text-sm">We&apos;ll reach out as soon as Cycle 2 applications open. Stay tuned!</p>
                </div>
              ) : (
                <form
                  id="cycle2-waitlist-form"
                  data-readdy-form
                  onSubmit={handleWaitlistSubmit}
                  className="space-y-5"
                >
                  {waitlistError && (
                    <div className="bg-[#E8553D]/10 border border-[#E8553D]/30 rounded-lg p-4">
                      <p className="text-[#E8553D] text-sm font-medium">{waitlistError}</p>
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={waitlistData.fullName}
                        onChange={handleWaitlistChange}
                        required
                        placeholder="Jane Smith"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all text-sm bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={waitlistData.email}
                        onChange={handleWaitlistChange}
                        required
                        placeholder="jane@example.com"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all text-sm bg-white"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={waitlistData.phone}
                        onChange={handleWaitlistChange}
                        placeholder="(205) 000-0000"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all text-sm bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                        Business Stage
                      </label>
                      <select
                        name="businessStage"
                        value={waitlistData.businessStage}
                        onChange={handleWaitlistChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all text-sm bg-white cursor-pointer"
                      >
                        <option value="">Select your stage</option>
                        <option value="Idea stage">Idea stage</option>
                        <option value="Side hustle (inconsistent)">Side hustle (inconsistent)</option>
                        <option value="Operating consistently">Operating consistently</option>
                        <option value="Established but needs structure">Established but needs structure</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      What&apos;s one thing you most want to fix in your business?
                    </label>
                    <textarea
                      name="topGoal"
                      value={waitlistData.topGoal}
                      onChange={handleWaitlistChange}
                      rows={3}
                      maxLength={500}
                      placeholder="e.g., I need a clear pricing structure and financial tracking system..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#1B3A5C]/20 focus:outline-none transition-all text-sm bg-white resize-none"
                    />
                    <p className="text-xs text-gray-400 mt-1">{waitlistData.topGoal.length}/500</p>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-[#1B3A5C] to-[#2A8C7A] text-white px-8 py-4 rounded-lg text-base font-bold hover:opacity-90 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Reserve My Spot for Cycle 2
                  </button>

                  <p className="text-center text-xs text-gray-400">
                    No spam, ever. We&apos;ll only reach out when Cycle 2 applications open.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-[#1B3A5C] to-[#0F2440] text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-10 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-4 mb-4">
                <img
                  src="https://static.readdy.ai/image/ac013c1ee8610b2ee6c1b91fe75f40f6/533b468db4011c652daa16bcc3a5c3fa.jpeg"
                  alt="From Scratch"
                  className="w-10 h-10 object-contain rounded"
                />
                <h3 className="text-2xl font-bold">From Scratch</h3>
              </div>
              <p className="text-[#F5C542] italic mb-4 text-sm">
                Stop guessing. Start operating.
              </p>
              <p className="text-white/60 leading-relaxed text-sm mb-6 max-w-md">
                In partnership with the Birmingham Business Resource Center
                (BBRC), we help entrepreneurs build strong operating systems,
                financial readiness, and back‑office structure.
              </p>
              <div className="flex items-center gap-3 mb-6">
                <img
                  src="https://static.readdy.ai/image/ac013c1ee8610b2ee6c1b91fe75f40f6/d3c89306c24de7dfc1bbb2f80f0a994f.png"
                  alt="BBRC"
                  className="h-10 object-contain bg-white rounded px-2 py-1"
                />
                <span className="text-white/40 text-xs">Official Partner</span>
              </div>
              <div className="flex gap-3">
                <a
                  href="#"
                  className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-[#E8553D] transition-colors cursor-pointer"
                  rel="nofollow"
                >
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a
                  href="#"
                  className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-[#E8553D] transition-colors cursor-pointer"
                  rel="nofollow"
                >
                  <i className="ri-instagram-line text-lg"></i>
                </a>
                <a
                  href="#"
                  className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-[#E8553D] transition-colors cursor-pointer"
                  rel="nofollow"
                >
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide mb-4 text-[#F5C542]">
                <a href="#contact">Contact</a>
              </h4>
              <div className="space-y-3 text-white/60 hover:text-[#E8553D] transition-colors cursor-pointer text-sm">
                <p>info@fromscratchbas.com</p>
                <p>(205)378-7941</p>
                <p>Birmingham, Alabama</p>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide mb-4 text-[#F5C542]">
                <a href="#links">Quick Links</a>
              </h4>
              <div className="space-y-3">
                <a
                  href="#about"
                  className="block text-white/60 hover:text-[#E8553D] transition-colors cursor-pointer text-sm"
                >
                  About
                </a>
                <a
                  href="#program"
                  className="block text-white/60 hover:text-[#E8553D] transition-colors cursor-pointer text-sm"
                >
                  Program
                </a>
                <a
                  href="#faq"
                  className="block text-white/60 hover:text-[#E8553D] transition-colors cursor-pointer text-sm"
                >
                  FAQ
                </a>
                <a
                  href="#waitlist"
                  className="block text-white/60 hover:text-[#E8553D] transition-colors cursor-pointer text-sm"
                >
                  Join Waitlist
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-xs">
              &copy; 2025 From Scratch Business Advisory Services. All rights
              reserved.
            </p>
            <div className="flex gap-6 text-xs">
              <a
                href="#"
                className="text-white/40 hover:text-white transition-colors cursor-pointer"
              >
                Privacy Policy
              </a>
              <a
                href="#"
                className="text-white/40 hover:text-white transition-colors cursor-pointer"
              >
                Terms of Service
              </a>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-white/40 hover:text-white transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Sticky CTA Bar */}
      {showStickyBar && (
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-[#1B3A5C] to-[#2A5A8C] shadow-2xl py-3 px-6 z-40 animate-slide-up">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="hidden sm:flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-[#F5C542] animate-pulse"></div>
              <p className="font-semibold text-white text-sm">
                Cycle 1 is closed — join the Cycle 2 waitlist now
              </p>
            </div>
            <div className="flex items-center gap-3 ml-auto">
              <button
                onClick={scrollToApplication}
                className="bg-[#E8553D] text-white px-6 py-2.5 rounded-md font-semibold hover:bg-[#D44A33] transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                Join Waitlist
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
